// Ruedita de configuración del composer de "Crear desde cero".
//
// Ocupa el lugar donde antes vivía el "+": ahí adentro van los ajustes de la
// generación, que cambian según el toggle Imagen/Video:
//   • Imagen → formato + cantidad (1 a 6)
//   • Video  → formato + duración
//
// En MOBILE el menú además repite el toggle Imagen/Video arriba de todo: el
// toggle principal vive en el header del chrome y es fácil no verlo, así que
// esta es la red de seguridad.
//
// NO usa overflow en ningún contenedor padre — la fila que lo contiene tiene
// prohibido el `overflow-*` justamente porque recorta este popover.

import React, { useState, useEffect } from 'react';
import { Settings2, Check, Minus, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import {
  VIDEO_DURATION_MIN,
  VIDEO_DURATION_MAX,
  MAX_CANTIDAD,
  type OutputKind,
} from './directMode';
import { OutputKindToggle } from './OutputKindToggle';
import { useIsMdUp } from './useIsMdUp';

export interface ConfiguracionMenuProps {
  outputKind: OutputKind;
  onChangeOutputKind: (k: OutputKind) => void;
  formatSlug: string;
  onChangeFormat: (id: string) => void;
  cantidad: number;
  onChangeCantidad: (n: number) => void;
  durationSeconds: number;
  onChangeDuration: (s: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ConfiguracionMenu: React.FC<ConfiguracionMenuProps> = ({
  outputKind,
  onChangeOutputKind,
  formatSlug,
  onChangeFormat,
  cantidad,
  onChangeCantidad,
  durationSeconds,
  onChangeDuration,
  open,
  onOpenChange,
}) => {
  const isMdUp = useIsMdUp();
  const [subOpen, setSubOpen] = useState(false);
  useEffect(() => {
    if (!open) setSubOpen(false);
  }, [open]);

  const formatName =
    FORMAT_OPTIONS_V2.find((f) => f.id === formatSlug)?.name ?? 'Cuadrado (1:1)';

  // Posición del thumb/progreso del slider de duración, en %.
  const durationPct =
    ((durationSeconds - VIDEO_DURATION_MIN) /
      (VIDEO_DURATION_MAX - VIDEO_DURATION_MIN)) *
    100;

  // Lista de formatos — sub-vista en mobile, flyout al costado en desktop.
  // SIN scroll: son 7 opciones y se muestran todas. Un `max-h` + overflow acá
  // obligaba a scrollear para ver Banner/Billboard, que es justo lo que se
  // quiere evitar en un selector corto.
  const formatContent = (showBack: boolean) => (
    <div className="flex flex-col gap-1">
      {showBack && (
        <button
          type="button"
          onClick={() => setSubOpen(false)}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
      )}
      {FORMAT_OPTIONS_V2.map((opt) => {
        const active = opt.id === formatSlug;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              onChangeFormat(opt.id);
              // Mobile vuelve al root; desktop deja el flyout abierto para
              // que se vea el tilde moverse.
              if (!isMdUp) setSubOpen(false);
            }}
            className={`w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition-colors ${
              active ? 'bg-[#1644EB0F]' : 'hover:bg-neutral-50'
            }`}
          >
            <span className="text-[14px] text-[#02062BE6]">{opt.name}</span>
            {active && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-label="Configuración de la generación"
        aria-expanded={open}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
          open
            ? 'bg-neutral-200 text-[#02062BE6]'
            : 'bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3]'
        }`}
      >
        <Settings2 className="w-4 h-4" strokeWidth={2.25} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[55]"
            onClick={() => onOpenChange(false)}
          />
          <div className="absolute bottom-full left-0 mb-2 w-[280px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 z-[56]">
            {isMdUp || !subOpen ? (
              <div className="flex flex-col">
                {/* Toggle Imagen|Video — SOLO mobile. En desktop el toggle de
                    arriba del input está siempre a la vista. */}
                <div className="md:hidden px-2 pt-1.5 pb-2.5 flex justify-center">
                  <OutputKindToggle
                    value={outputKind}
                    onChange={onChangeOutputKind}
                    className="w-full"
                  />
                </div>
                <div className="md:hidden border-t border-black/[0.05] mb-1" />

                {/* Formato → flyout en desktop, sub-vista en mobile. */}
                <button
                  type="button"
                  onClick={() => setSubOpen((v) => !v)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl text-left transition-colors ${
                    subOpen && isMdUp
                      ? 'bg-neutral-100'
                      : 'hover:bg-neutral-50 active:bg-neutral-100'
                  }`}
                >
                  <span className="text-[14.5px] text-[#02062BE6]">Formato</span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-[13.5px] text-[#3C3C43]/55 truncate max-w-[130px]">
                      {formatName}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40" />
                  </span>
                </button>

                {outputKind === 'imagen' ? (
                  /* Cantidad — stepper +/-, mismo control que InterfazV2. */
                  <div className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl">
                    <span className="min-w-0">
                      <span className="block text-[14.5px] text-[#02062BE6]">
                        Cantidad
                      </span>
                      <span className="block text-[12px] text-[#3C3C43]/55 mt-0.5 leading-snug">
                        Cada imagen consume créditos aparte.
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        aria-label="Menos"
                        disabled={cantidad <= 1}
                        onClick={() => onChangeCantidad(Math.max(1, cantidad - 1))}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                      <span className="w-6 text-center text-[14px] font-semibold text-[#02062BE6] tabular-nums">
                        {cantidad}
                      </span>
                      <button
                        type="button"
                        aria-label="Más"
                        disabled={cantidad >= MAX_CANTIDAD}
                        onClick={() =>
                          onChangeCantidad(Math.min(MAX_CANTIDAD, cantidad + 1))
                        }
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                    </span>
                  </div>
                ) : (
                  /* Duración — slider. El riel se dibuja a mano y el
                     `input[type=range]` va invisible encima, que es el patrón
                     del slider de tiers del pricing (PlanCard): así se
                     conserva el teclado y el arrastre nativos sin pelear con
                     los estilos por navegador. */
                  <div className="w-full px-3 py-2.5">
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                      <span className="text-[14.5px] text-[#02062BE6]">
                        Duración
                      </span>
                      <span className="text-[13.5px] font-semibold text-brand-primary tabular-nums">
                        {durationSeconds}s
                      </span>
                    </div>
                    <div className="relative h-5 flex items-center">
                      {/* Riel */}
                      <div className="absolute inset-x-0 h-1.5 rounded-full bg-neutral-200" />
                      {/* Progreso */}
                      <div
                        className="absolute left-0 h-1.5 rounded-full bg-brand-primary"
                        style={{ width: `${durationPct}%` }}
                      />
                      {/* Thumb */}
                      <div
                        className="absolute w-4 h-4 rounded-full bg-white border-2 border-brand-primary shadow-[0_1px_3px_rgba(0,0,0,0.2)] pointer-events-none"
                        style={{
                          left: `calc(${durationPct}% - 8px)`,
                        }}
                      />
                      <input
                        type="range"
                        min={VIDEO_DURATION_MIN}
                        max={VIDEO_DURATION_MAX}
                        step={1}
                        value={durationSeconds}
                        onChange={(e) => onChangeDuration(Number(e.target.value))}
                        aria-label="Duración del video en segundos"
                        aria-valuetext={`${durationSeconds} segundos`}
                        className="absolute inset-0 size-full cursor-pointer opacity-0"
                        style={{ touchAction: 'none' }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-[#3C3C43]/45 tabular-nums">
                        {VIDEO_DURATION_MIN}s
                      </span>
                      <span className="text-[11px] text-[#3C3C43]/45 tabular-nums">
                        {VIDEO_DURATION_MAX}s
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              formatContent(true)
            )}
            {/* DESKTOP: flyout al costado. Se abre hacia la DERECHA porque
                esta ruedita está anclada al borde izquierdo del composer. */}
            {isMdUp && subOpen && (
              <div className="absolute left-full bottom-0 ml-2 w-[240px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-1.5 animate-mode-swap">
                {formatContent(false)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConfiguracionMenu;
